// inv/src/components/ui/datePicker.jsx
import { useState, useRef, useEffect } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { format } from "date-fns";
import "react-day-picker/style.css";

const DatePicker = ({ onDateChange, placeholder = "Pick a date", selectedDate }) => {
  const [internalDate, setInternalDate] = useState(selectedDate || null);
  const [isOpen, setIsOpen] = useState(false);
  const [openDirection, setOpenDirection] = useState("down");
  const ref = useRef(null);
  const defaultClassNames = getDefaultClassNames();

  useEffect(() => {
    setInternalDate(selectedDate ?? null);
  }, [selectedDate]);

  const handleDayClick = (day) => {
    setInternalDate(day);
    setIsOpen(false);
    onDateChange?.(day);
  };

  const toggleCalendar = () => {
    if (!isOpen) {
      const buttonRect = ref.current?.getBoundingClientRect();
      const viewportHeight = typeof window !== "undefined" ? window.innerHeight : Number.POSITIVE_INFINITY;
      const spaceBelow = buttonRect ? viewportHeight - buttonRect.bottom : Number.POSITIVE_INFINITY;
      const calendarHeight = 250;
      setOpenDirection(spaceBelow < calendarHeight ? "up" : "down");
    }
    setIsOpen(prev => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative w-fit" ref={ref}>
      <button
        onClick={toggleCalendar}
        className="w-full cursor-pointer rounded-lg border bg-white px-2 py-1 text-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-1"
        type="button"
      >
        {internalDate ? format(internalDate, "d MMM yyyy") : placeholder}
      </button>

      {isOpen && (
        <div
          className={`absolute z-10 text-xs rounded-md border bg-white shadow-md p-2 ${openDirection === "down" ? "mt-2" : "mb-2 bottom-full"}`}
        >
          <DayPicker
            mode="single"
            selected={internalDate}
            onSelect={handleDayClick}
            fixedWeeks
            showOutsideDays
            classNames={{
              root: `${defaultClassNames.root} relative p-2 w-[288px]`,
              caption: `absolute top-2 left-2 right-2 flex items-center justify-between`,
              months: `pt-10`,
              day: `w-8 h-8 flex-1 rounded-md hover:bg-stone-200`,
              today: `bg-gray-100`,
              selected: `bg-black rounded-md text-white`,
              day_button: `cursor-pointer w-8 h-8`,
              chevron: `w-6 fill-black`,
              caption_label: `text-sm`,
            }}
          />
        </div>
      )}
    </div>
  );
};

export { DatePicker };
